/**
 * Audio Analysis Utility - Optimized for speed
 * BPM: sliced to first 30s before analysis (~6x faster for long tracks)
 * Key: 30 frames at fftSize=4096 (~8x faster than original 200 frames at 8192)
 */

import { guess } from 'web-audio-beat-detector';

// Krumhansl-Schmuckler key profiles (scientifically validated)
const KEY_PROFILES = {
  major: [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88],
  minor: [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17]
};

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// ─── BPM ──────────────────────────────────────────────────────────────────────

/**
 * Slice an AudioBuffer to at most `maxSeconds` seconds.
 * Creates a new buffer so the original is untouched.
 */
function sliceAudioBuffer(audioBuffer, audioContext, maxSeconds = 30) {
  const maxSamples = Math.min(audioBuffer.length, Math.floor(maxSeconds * audioBuffer.sampleRate));
  const sliced = audioContext.createBuffer(
    audioBuffer.numberOfChannels,
    maxSamples,
    audioBuffer.sampleRate
  );
  for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
    sliced.copyToChannel(audioBuffer.getChannelData(ch).subarray(0, maxSamples), ch);
  }
  return sliced;
}

/**
 * Detect BPM — analyses only the first 30 s for speed.
 */
export async function detectBPMOnly(audioFile) {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const arrayBuffer = await audioFile.arrayBuffer();
    const fullBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Slice to first 30 s — enough for reliable BPM, much faster for long tracks
    const analysisBuffer = sliceAudioBuffer(fullBuffer, audioContext, 30);

    const result = await guess(analysisBuffer, { minTempo: 60, maxTempo: 200 });
    return { bpm: Math.round(result.bpm), success: true };
  } catch (error) {
    console.error('BPM detection error:', error);
    try {
      return await detectBPMManual(audioFile);
    } catch {
      return { bpm: null, success: false, error: error.message };
    }
  } finally {
    audioContext.close();
  }
}

/**
 * Manual BPM fallback — analyses first 30 s only.
 */
async function detectBPMManual(audioFile) {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const arrayBuffer = await audioFile.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;
    const maxSamples = Math.min(channelData.length, 30 * sampleRate);

    const frameSize = Math.floor(sampleRate * 0.02);
    const hopSize = Math.floor(frameSize / 2);
    const numFrames = Math.floor((maxSamples - frameSize) / hopSize);
    const energy = new Float32Array(numFrames);

    for (let i = 0; i < numFrames; i++) {
      const start = i * hopSize;
      let sum = 0;
      for (let j = 0; j < frameSize; j++) {
        const s = channelData[start + j];
        sum += s * s;
      }
      energy[i] = Math.sqrt(sum / frameSize);
    }

    const peaks = [];
    const windowSize = 5;
    for (let i = windowSize; i < energy.length - windowSize; i++) {
      let isMax = true, localAvg = 0;
      for (let j = -windowSize; j <= windowSize; j++) {
        localAvg += energy[i + j];
        if (j !== 0 && energy[i + j] >= energy[i]) isMax = false;
      }
      localAvg /= (windowSize * 2 + 1);
      if (isMax && energy[i] > localAvg * 1.3) peaks.push(i);
    }

    const intervals = [];
    for (let i = 1; i < peaks.length; i++) {
      const bpm = 60 / ((peaks[i] - peaks[i - 1]) * hopSize / sampleRate);
      if (bpm >= 60 && bpm <= 200) intervals.push(Math.round(bpm));
    }

    if (intervals.length < 3) return { bpm: null, success: false };

    const histogram = {};
    for (const bpm of intervals) {
      const bin = Math.round(bpm / 2) * 2;
      histogram[bin] = (histogram[bin] || 0) + 1;
    }

    let maxCount = 0, detectedBPM = null;
    for (const [bpm, count] of Object.entries(histogram)) {
      if (count > maxCount) { maxCount = count; detectedBPM = parseInt(bpm); }
    }

    return { bpm: detectedBPM, success: detectedBPM !== null };
  } finally {
    audioContext.close();
  }
}

// ─── Key ──────────────────────────────────────────────────────────────────────

/**
 * Detect musical key.
 * Analyses first 25 s using 30 frames at fftSize=4096 — ~8x faster than original.
 */
export async function detectKeyOnly(audioFile) {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const arrayBuffer = await audioFile.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const chromagram = computeChromagram(audioBuffer);
    const { key, scale, correlation } = detectKeyFromChromagram(chromagram);
    return { key, scale, fullKey: `${key} ${scale}`, success: true, correlation };
  } catch (error) {
    console.error('Key detection error:', error);
    return { key: null, scale: null, fullKey: null, success: false, error: error.message };
  } finally {
    audioContext.close();
  }
}

/**
 * Chromagram computation — optimised:
 * - 25 s analysis window (was 60 s)
 * - 30 frames (was 200)
 * - fftSize 4096 (was 8192)  →  Goertzel cost halved
 */
function computeChromagram(audioBuffer) {
  const sampleRate = audioBuffer.sampleRate;
  const channelData = audioBuffer.getChannelData(0);

  // Start slightly into the track to skip silence / intros
  const startOffset = Math.min(3 * sampleRate, Math.floor(channelData.length * 0.05));
  const analyzeLength = Math.min(25 * sampleRate, channelData.length - startOffset);

  const chromagram = new Float32Array(12);
  const fftSize = 4096;   // was 8192
  const hopSize = 2048;
  const numFrames = Math.floor((analyzeLength - fftSize) / hopSize);
  const maxFrames = Math.min(numFrames, 30); // was 200

  const freqResolution = sampleRate / fftSize;
  const C0 = 440 * Math.pow(2, -4.75);

  for (let frame = 0; frame < maxFrames; frame++) {
    const frameStart = startOffset + frame * hopSize;

    // Hann window
    const frameData = new Float32Array(fftSize);
    for (let i = 0; i < fftSize; i++) {
      frameData[i] = (channelData[frameStart + i] || 0) *
        0.5 * (1 - Math.cos(2 * Math.PI * i / (fftSize - 1)));
    }

    // Goertzel for each pitch class across octaves 2–5
    for (let pitchClass = 0; pitchClass < 12; pitchClass++) {
      for (let octave = 2; octave <= 5; octave++) {
        const freq = C0 * Math.pow(2, (octave * 12 + pitchClass) / 12);
        if (freq < 65 || freq > 2000) continue;

        const k = freq / freqResolution;
        const coeff = 2 * Math.cos(2 * Math.PI * k / fftSize);
        let s1 = 0, s2 = 0;
        for (let n = 0; n < fftSize; n++) {
          const s0 = frameData[n] + coeff * s1 - s2;
          s2 = s1; s1 = s0;
        }
        const power = s1 * s1 + s2 * s2 - coeff * s1 * s2;
        const octaveWeight = (octave === 3 || octave === 4) ? 1.5 : 1.0;
        chromagram[pitchClass] += Math.sqrt(Math.abs(power)) * octaveWeight;
      }
    }
  }

  // Normalise
  const maxVal = Math.max(...chromagram);
  if (maxVal > 0) for (let i = 0; i < 12; i++) chromagram[i] /= maxVal;
  return chromagram;
}

function detectKeyFromChromagram(chromagram) {
  let bestKey = 'C', bestScale = 'Major', bestCorr = -Infinity;

  for (let pc = 0; pc < 12; pc++) {
    const rotated = new Float32Array(12);
    for (let i = 0; i < 12; i++) rotated[i] = chromagram[(i + pc) % 12];

    const majorCorr = pearsonCorrelation(rotated, KEY_PROFILES.major);
    if (majorCorr > bestCorr) { bestCorr = majorCorr; bestKey = NOTE_NAMES[pc]; bestScale = 'Major'; }

    const minorCorr = pearsonCorrelation(rotated, KEY_PROFILES.minor);
    if (minorCorr > bestCorr) { bestCorr = minorCorr; bestKey = NOTE_NAMES[pc]; bestScale = 'Minor'; }
  }

  return { key: bestKey, scale: bestScale, correlation: bestCorr };
}

function pearsonCorrelation(a, b) {
  const n = a.length;
  let sumA = 0, sumB = 0, sumAB = 0, sumA2 = 0, sumB2 = 0;
  for (let i = 0; i < n; i++) {
    sumA += a[i]; sumB += b[i]; sumAB += a[i] * b[i];
    sumA2 += a[i] * a[i]; sumB2 += b[i] * b[i];
  }
  const num = n * sumAB - sumA * sumB;
  const den = Math.sqrt((n * sumA2 - sumA * sumA) * (n * sumB2 - sumB * sumB));
  return den === 0 ? 0 : num / den;
}

// ─── Combined ─────────────────────────────────────────────────────────────────

export async function analyzeAudio(audioFile, onProgress = null) {
  if (onProgress) onProgress('Analyzing...');
  const [bpmResult, keyResult] = await Promise.all([
    detectBPMOnly(audioFile),
    detectKeyOnly(audioFile)
  ]);
  return {
    bpm: bpmResult.bpm,
    key: keyResult.key,
    scale: keyResult.scale,
    fullKey: keyResult.fullKey
  };
}
